import { t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/card';
import { RefreshCw } from 'lucide-react';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { OwnersTable } from '@/components/tables/owners-table';
import {
  type TOwnersTableFilters,
  ZOwnerTableFiltersSchema,
} from '@/components/tables/owners-table-filters';
import { trpc } from '@/providers/trpc';
import type { RouterOutputs } from '@/providers/trpc-client';

export function OwnersPage() {
  useLocale();

  const navigate = useNavigate();

  const [params, setParams] = useQueryStates(
    {
      page: parseAsInteger.withDefault(1),
      perPage: parseAsInteger.withDefault(10),
      search: parseAsString.withDefault(''),
    },
    {
      history: 'replace',
      shallow: false,
    },
  );
  const filters: TOwnersTableFilters = useMemo(() => {
    try {
      return ZOwnerTableFiltersSchema.parse(params);
    } catch {
      return ZOwnerTableFiltersSchema.parse({});
    }
  }, [getLocale(), params]);

  const handleFilterChange = useCallback(
    (key: keyof TOwnersTableFilters, value: string | undefined) => {
      setParams({
        [key]: value === undefined || value === '' ? null : value,
      });
    },
    [setParams],
  );

  const handleClearFilters = useCallback(() => {
    setParams({ search: '' });
  }, [setParams]);

  const {
    data: ownerUsersData,
    isFetching,
    isRefetching,
    isLoading: isOrganizationsLoading,
    refetch,
    isError,
    error,
  } = trpc.admin.listOwnerUsers.useQuery({
    page: params.page,
    perPage: params.perPage,
    email: params.search || undefined,
  });
  const isLoading = isFetching || isOrganizationsLoading || isRefetching;

  const ownerUsers: RouterOutputs['admin']['listOwnerUsers'] = ownerUsersData ?? {
    data: [],
    perPage: 10,
    page: 1,
    totalPages: 1,
    total: 0,
  };
  return (
    <div className="flex-1 space-y-6 p-4 pt-6 md:p-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-bold text-3xl tracking-tight">{t('workspace.owners')}</h1>
          <p className="mt-1 text-muted-foreground">
            {t('auth.manageOrganizationOwnersAndTheirAccountStatus')}{' '}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button disabled={isLoading} onClick={() => refetch()} variant="outline">
            <RefreshCw className={`me-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            {t('auth.refresh')}{' '}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('auth.allOwners')}</CardTitle>
          <CardDescription>
            {isLoading
              ? t('auth.loadingOwners')
              : t('count.owners_other', { count: ownerUsers.data.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <OwnersTable
            data={ownerUsers.data}
            error={error}
            filters={params}
            isError={isError}
            isLoading={isLoading}
            onClearFilters={handleClearFilters}
            onFilterChange={handleFilterChange}
            onRefetch={refetch}
          />
        </CardContent>
      </Card>
    </div>
  );
}
